import { useState, useEffect } from 'react';
import { AlertTriangle, Play, Trash2, Database, RefreshCw, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface QARun {
  id: string;
  created_at: string;
  environment: string;
  status: string;
  summary_passed_count: number;
  summary_failed_count: number;
  duration_ms: number;
  notes: string | null;
}

interface QATestResult {
  id: string;
  test_suite: string;
  test_name: string;
  status: string;
  details: any;
  duration_ms: number;
}

interface QALog {
  id: string;
  level: string;
  message: string;
  context: any;
  created_at: string;
}

export function QAManagement() {
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState<QARun[]>([]);
  const [currentRun, setCurrentRun] = useState<QARun | null>(null);
  const [testResults, setTestResults] = useState<QATestResult[]>([]);
  const [logs, setLogs] = useState<QALog[]>([]);
  const [expandedTest, setExpandedTest] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [environment, setEnvironment] = useState('dev');

  const [allowTestData, setAllowTestData] = useState(false);

  useEffect(() => {
    loadRuns();
    detectEnvironment();
  }, []);

  const detectEnvironment = () => {
    const url = window.location.hostname;
    if (url.includes('localhost') || url.includes('127.0.0.1')) {
      setEnvironment('dev');
    } else if (url.includes('staging')) {
      setEnvironment('staging');
    } else {
      setEnvironment('prod');
    }
  };

  const loadRuns = async () => {
    try {
      const { data, error } = await supabase
        .from('qa_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setRuns(data || []);
    } catch (error: any) {
      console.error('Error loading runs:', error);
    }
  };

  const loadRunDetails = async (runId: string) => {
    try {
      const { data: run } = await supabase
        .from('qa_runs')
        .select('*')
        .eq('id', runId)
        .single();

      const { data: results } = await supabase
        .from('qa_test_results')
        .select('*')
        .eq('qa_run_id', runId)
        .order('started_at', { ascending: true });

      const { data: runLogs } = await supabase
        .from('qa_logs')
        .select('*')
        .eq('qa_run_id', runId)
        .order('created_at', { ascending: true });

      setCurrentRun(run);
      setTestResults(results || []);
      setLogs(runLogs || []);
    } catch (error: any) {
      console.error('Error loading run details:', error);
    }
  };

  const handleSeedData = async () => {
    if (!allowTestData && environment === 'prod') {
      alert('Test data generation is disabled in production');
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qa-seed-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            environment,
            allowInProduction: allowTestData && environment === 'prod'
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert(`Données de test générées avec succès!\n\nUtilisateurs: ${result.results.users.length}\nOrganisations: ${result.results.organizations.length}\nTransactions: ${result.results.transactions.reduce((sum: number, t: any) => sum + t.count, 0)}`);
      } else {
        alert(`Erreur: ${result.error}`);
      }
    } catch (error: any) {
      alert(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRunTests = async (suites: string[] = ['all']) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qa-run-tests`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ suites, environment }),
        }
      );

      const result = await response.json();

      if (result.success) {
        await loadRuns();
        await loadRunDetails(result.runId);
        alert(`Tests exécutés avec succès!\n\nPassés: ${result.summary.passed}\nÉchoués: ${result.summary.failed}\nDurée: ${(result.summary.duration_ms / 1000).toFixed(2)}s`);
      } else {
        alert(`Erreur: ${result.error}`);
      }
    } catch (error: any) {
      alert(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qa-cleanup-data`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ deleteRuns: false }),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert(`Nettoyage effectué!\n\nUtilisateurs: ${result.results.users}\nTransactions: ${result.results.transactions}\nProfils: ${result.results.profiles}`);
        await loadRuns();
      } else {
        alert(`Erreur: ${result.error}`);
      }
    } catch (error: any) {
      alert(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'skipped':
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-blue-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'passed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'skipped':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="space-y-6">
      {environment === 'prod' && (
        <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Environnement Production</h3>
              <p className="text-sm text-red-700 mt-1">
                La génération de données de test est désactivée en production. Seuls les tests non-destructifs sont autorisés.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">QA / Recette</h2>
        <p className="text-gray-600 mb-6">
          Module de tests automatisés pour valider les fonctionnalités et générer des données de test.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <button
            onClick={handleSeedData}
            disabled={loading || (!allowTestData && environment === 'prod')}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Database className="h-5 w-5" />
            <span>Générer données de test</span>
          </button>

          <button
            onClick={() => handleRunTests(['all'])}
            disabled={loading}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-5 w-5" />
            <span>Exécuter tous les tests</span>
          </button>

          <button
            onClick={() => handleRunTests(['permissions', 'ui_routing'])}
            disabled={loading}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-5 w-5" />
            <span>Tests Permissions/UI</span>
          </button>

          <button
            onClick={() => handleRunTests(['calculs', 'performance'])}
            disabled={loading}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-5 w-5" />
            <span>Tests Calculs</span>
          </button>

          <button
            onClick={handleCleanup}
            disabled={loading}
            className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-colors ${
              showDeleteConfirm
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            <Trash2 className="h-5 w-5" />
            <span>{showDeleteConfirm ? 'Confirmer suppression' : 'Nettoyer données'}</span>
          </button>

          <button
            onClick={loadRuns}
            disabled={loading}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className="h-5 w-5" />
            <span>Rafraîchir</span>
          </button>
        </div>

        {environment === 'prod' && (
          <div className="mb-6">
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={allowTestData}
                onChange={(e) => setAllowTestData(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span>Autoriser injection données de test (prod uniquement)</span>
            </label>
          </div>
        )}
      </div>

      {currentRun && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Dernier run</h3>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentRun.status)}`}>
              {currentRun.status}
            </span>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-900">{currentRun.summary_passed_count}</div>
              <div className="text-sm text-green-700">Passés</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-900">{currentRun.summary_failed_count}</div>
              <div className="text-sm text-red-700">Échoués</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-900">
                {((currentRun.duration_ms || 0) / 1000).toFixed(2)}s
              </div>
              <div className="text-sm text-blue-700">Durée</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900">{testResults.length}</div>
              <div className="text-sm text-gray-700">Total tests</div>
            </div>
          </div>

          <div className="space-y-2">
            {testResults.map((test) => (
              <div key={test.id} className="border rounded-lg">
                <button
                  onClick={() => setExpandedTest(expandedTest === test.id ? null : test.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(test.status)}
                    <div className="text-left">
                      <div className="font-medium text-gray-900">{test.test_name}</div>
                      <div className="text-sm text-gray-500">
                        {test.test_suite} • {test.duration_ms}ms
                      </div>
                    </div>
                  </div>
                  {expandedTest === test.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {expandedTest === test.id && (
                  <div className="px-4 pb-4 border-t bg-gray-50">
                    <pre className="text-xs text-gray-700 overflow-x-auto mt-2 p-3 bg-white rounded">
                      {JSON.stringify(test.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {runs.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des runs</h3>
          <div className="space-y-2">
            {runs.map((run) => (
              <button
                key={run.id}
                onClick={() => loadRunDetails(run.id)}
                className="w-full flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <div>
                  <div className="font-medium text-gray-900">
                    {new Date(run.created_at).toLocaleString('fr-FR')}
                  </div>
                  <div className="text-sm text-gray-500">
                    {run.summary_passed_count} passés • {run.summary_failed_count} échoués • {((run.duration_ms || 0) / 1000).toFixed(2)}s
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(run.status)}`}>
                  {run.status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
